import { useEffect, useMemo, useRef, useState } from 'react';
import { markNewActe } from '@/lib/actesNew';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { friendlyError } from '@/lib/friendlyError';
import {
  actesCreateDemande, actesUpdateDemande, actesTrashDemande, actesUpload,
  actesAddDocument, actesFileLink, actesReceptionInfo, actesAttachReceived, actesCreateType, actesSaveAuthority,
  actesTurnaround, type ActeRow, type ActesReference, type ActesTurnaround, type ActesPerson,
} from '@/integrations/actesApi';
import { PeopleField, peopleReader } from './ActesPerson';
import { ActeDocuments, type StagedDoc } from './ActeDocuments';
import { DocViewer } from './DocViewer';
import { Combobox } from './Combobox';
import { byDossierRank } from './rank';
import { pushUndo } from '@/lib/actesUndo';
import { setActesFocus } from '@/lib/actesFocus';
import { fetchTicketById } from '@/integrations/hubspot';
import { actesDossier, actesDossiers } from '@/integrations/actesApi';
import { useTidyName, dossierName } from '@/hooks/useTidyName';
import { countryOfRef, actesEn } from '@/lib/actesCountry';

/**
 * A demande d'acte, created or corrected.
 *
 * The fields are the old site's, unchanged, because they are the ones the
 * administration asks for: who the act concerns, which act, from which
 * authority, where it stands, and the four dates that decide whether a dossier
 * can be deposited — demandée, émise, reçue and, derived from the emission,
 * when it expires.
 */
export function ActeModal({ lang, reference, dossierId, initial, seed, onTicket, onClose }: {
  lang: Lang;
  reference: ActesReference;
  dossierId?: string | null;
  initial?: ActeRow | null;
  /** UNE LIGNE DE « CE QU'IL MANQUE » EST UNE DEMANDE QUI N'EXISTE PAS.
      Cliquer dessus ouvre ce formulaire avec la personne et le type déjà
      écrits : c'est la seule information que la ligne portait, et la retaper
      était le geste qui décourageait de s'en servir. */
  seed?: { person: string; type: string } | null;
  /** The case whose page this was opened from, when it was opened from one.
   *  It is the only thing that tells the form "you are already there" — which
   *  is what makes the link to the case disappear exactly where it would be a
   *  loop, and stay everywhere else. */
  onTicket?: string | null;
  onClose: () => void;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const qc = useQueryClient();
  const editing = !!initial;

  const [numero, setNumero] = useState(initial?.numero ?? '');
  const [person, setPerson] = useState(initial?.person_concerned ?? seed?.person ?? '');
  /* La semence donne un LIBELLÉ de type ; on retrouve son id dans la
     référence. S'il ne correspond à rien, on n'invente pas : le champ reste
     vide et la personne choisit. */
  const seededType = seed?.type
    ? reference.acteTypes.find((t) => t.label_fr.toLowerCase() === seed.type.toLowerCase()
        || (t.label_short_fr ?? '').toLowerCase() === seed.type.toLowerCase())?.id ?? ''
    : '';
  const [typeId, setTypeId] = useState<number | ''>(initial?.acte_type_id ?? seededType);
  const [authorityId, setAuthorityId] = useState<number | ''>(initial?.authority_id ?? '');
  /* PORTUGAL: the document asked for is written in words (a «Certidão de
     nascimento» from a Conservatória, a criminal record from the country of
     birth…); a type from the shared list is optional, and there is no
     register of French authorities. A request always sits in a dossier. */
  const pt = countryOfRef(reference) === 'PT';
  const [requestLine, setRequestLine] = useState(initial?.request_line ?? (pt ? seed?.type ?? '' : ''));
  const [statusId, setStatusId] = useState<number | ''>(
    initial?.status_id ?? reference.acteStatuses.find((s) => s.code === 'a_demander_a_l_adm')?.id ?? '',
  );
  /* Yours until you hand it over — the portal knows who is asking. Several
     people when it is a shared job: the first is the lead (`assigned_to`),
     everyone is in `assigned_emails`. */
  const who = useMemo(() => peopleReader(reference.profiles), [reference.profiles]);
  const [team, setTeam] = useState<ActesPerson[]>(() => (initial
    ? who.people(initial)
    : reference.profiles.filter((p) => p.id === reference.me.id)));
  const [reference_, setReference] = useState(initial?.reference ?? '');
  const [channel, setChannel] = useState<'' | 'courrier' | 'email'>(initial?.demande_channel ?? '');
  const [dDemande, setDDemande] = useState(initial?.date_demande ?? '');
  const [dEmission, setDEmission] = useState(initial?.date_emission ?? '');
  const [dReception, setDReception] = useState(initial?.date_reception ?? '');
  const [dExpiration, setDExpiration] = useState(initial?.date_expiration ?? '');
  const [comments, setComments] = useState(initial?.comments ?? '');
  const [foreign, setForeign] = useState(initial?.is_foreign === true);
  /* Le code de série d'une e-photo. Il vient de l'image au scan ; ici on peut
     le corriger ou le poser à la main quand la photo est arrivée autrement. */
  const [ephoto, setEphoto] = useState(initial?.ephoto_code ?? '');
  /* LES DÉLAIS OBSERVÉS, SOUS L'AUTORITÉ CHOISIE.
     « Cette mairie tarde » se disait de mémoire ; le portail a les dates de
     départ et de réception, donc la médiane. Une seule lecture par ouverture
     du formulaire, mise en cache cinq minutes : le calcul est léger et la
     donnée change lentement. */
  const turnaround = useQuery<ActesTurnaround>({
    queryKey: ['actes', 'turnaround'],
    queryFn: ({ signal }) => actesTurnaround(signal),
    staleTime: 5 * 60_000,
    retry: false,
    enabled: !pt,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  /* Which fields the last attempt found empty — they mark themselves. */
  const [missing, setMissing] = useState<string[]>([]);

  /* Files attached before the demande exists: already in storage, held here
     until it has an id. */
  const [stagedDocs, setStagedDocs] = useState<StagedDoc[]>([]);

  /* The received act itself — who put it there, and the manual way in when the
     reception step could not read the scan. */
  const [recv, setRecv] = useState<{ file_name: string | null; uploader_name: string | null; created_at: string } | null>(null);
  const [recvDates, setRecvDates] = useState(true);
  const [recvNote, setRecvNote] = useState('');
  const [recvBusy, setRecvBusy] = useState(false);
  const [recvKey, setRecvKey] = useState(0);
  /* What was just created, so the form can say so and offer the two things
     anybody wants next: go and see it, or file the next one. */
  const [made, setMade] = useState<{ id: string; person: string; dossierId: string | null } | null>(null);

  /* WHOSE CASE IS THIS DEMANDE FOR. When the dossier is tied to a client's
     case, the form says which one — the client's name as it is actually read,
     and the service — so nobody files a certificate on the wrong family. */
  /* WHICH DOSSIER THIS DEMANDE BELONGS TO — and it must be changeable.
     Filed on the wrong family, or filed loose and now knowing whose it is:
     both are everyday, and until now neither could be corrected anywhere in
     the portal. Opened from inside a dossier it starts there; opened from the
     Demandes tab it starts empty, which is what "sans dossier" means. */
  const [onDossier, setOnDossier] = useState<string>(dossierId ?? initial?.dossier_id ?? '');
  /* Pressed "change": the picker comes back for this one edit. */
  const [swapDossier, setSwapDossier] = useState(false);
  /* What "change" is changing FROM. The picker clears the selection on the
     first keystroke (typing over a name unselects it, on purpose), so the
     previous id has to be kept here or "keep the previous one" has nothing
     to keep. */
  const prevDossier = useRef('');
  const on = onDossier || null;
  const dossiers = useQuery({
    queryKey: ['actes', 'dossiers'],
    queryFn: ({ signal }) => actesDossiers(signal),
    staleTime: 5 * 60_000,
  });

  const dossier = useQuery({
    queryKey: ['actes', 'dossier', on],
    queryFn: ({ signal }) => actesDossier(on as string, signal),
    enabled: !!on,
    staleTime: 60_000,
    retry: false,
  });
  const tie = dossier.data?.dossier.ticket_id ?? dossier.data?.dossier.hubspot_id ?? null;
  const ticket = useQuery({
    queryKey: ['hs-ticket', tie],
    queryFn: () => fetchTicketById(pt ? 'PT' : 'FR', tie as string),
    enabled: !!tie,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const tidy = useTidyName(ticket.data);
  const recvInput = useRef<HTMLInputElement>(null);
  const [view, setView] = useState<(() => Promise<{ url: string; name: string | null; mime?: string | null }>) | null>(null);

  useEffect(() => {
    if (!initial?.id) return;
    let live = true;
    actesReceptionInfo(initial.id).then((r) => { if (live) setRecv(r); }).catch(() => {});
    return () => { live = false; };
  }, [initial?.id, recvKey]);

  /* ── WHAT THE FORM ALREADY KNOWS ──
     Opened from a dossier, the dossier is known; and through it the client's
     case, which the model has read the name of. Two fields follow from that
     and were left blank anyway, to be typed by hand on every single request:
       · the person concerned — a family asks for six certificates and the
         first one is nearly always the client themselves;
       · the family order — the first request on an empty dossier is "1".
     Both are DEFAULTS, in a normal editable field: typing over them is the
     expected thing, not a fight. Nothing else is filled — the type, the
     authority and the dates are not knowable from here, and guessing them
     would be inventing. Only on creation, only while the field is untouched,
     and never on a dossier that already carries requests. */
  /* What WE put there, so we may replace it, and only that. A value the
     person typed is never touched again — not when the dossier changes, not
     when the model finishes reading the name. */
  const autoPerson = useRef('');
  const autoNumero = useRef('');
  useEffect(() => {
    if (editing) return;
    const d = dossier.data;
    if (!d) return;
    /* The dossier's stored name may itself be a filing string (dossiers
       created before the model read their subject were saved with one), so it
       goes through the same repair as everywhere else — otherwise "person
       concerned" would be prefilled with
       "Jacques McCormack - Long Stay Visa (Bronze Package) France - (…)".
       And we WAIT for the reading rather than latching on the first render:
       filling the field with the mangle and then leaving it there because a
       flag said "done" is worse than not filling it at all. */
    if (tidy.reading) return;
    const client = dossierName(d.dossier.name, ticket.data?.subject, tidy.full).trim()
      || (tidy.client || '').trim();
    if (client && (!person.trim() || person === autoPerson.current)) {
      setPerson(client);
      autoPerson.current = client;
    }
    const first = (d.actes?.length ?? 0) === 0 ? '1' : '';
    if (first && (!numero.trim() || numero === autoNumero.current)) {
      setNumero(first);
      autoNumero.current = first;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossier.data, ticket.data?.subject, tidy.full, tidy.client, tidy.reading, editing]);

  const openReceived = () => { if (initial) setView(() => () => actesFileLink(initial.id)); };

  /* A new received document NEVER overwrites the one already there: it joins
     the list. Reading the dates is optional — a super admin who is only
     filing a copy does not want the four dates rewritten. */
  const attachReceived = async (f: File) => {
    if (!initial) return;
    setRecvBusy(true); setRecvNote(''); setErr('');
    try {
      const up = await actesUpload(f, '_intake');
      const r = await actesAttachReceived(initial.id, {
        file_path: up.path, file_name: up.name, file_mime: up.mime, skip_dates: !recvDates,
      });
      setRecvNote(r.skipped
        ? T('Document joint (dates inchangées).', 'Document attached (dates unchanged).')
        : r.read
          ? T('Document lu — dates mises à jour.', 'Document read — dates updated.')
          /* Le document est joint quoi qu'il arrive ; ce qui manquait, c'est
             POURQUOI la lecture a échoué. Le serveur le disait, personne ne
             l'écoutait. */
          : [T('Document joint ; la lecture n’a rien pu tirer.', 'Document attached; the reading found nothing.'),
             r.detail].filter(Boolean).join(' — '));
      setRecvKey((k) => k + 1);
      await qc.invalidateQueries({ queryKey: ['actes'] });
    } catch (e) {
      const m = e instanceof Error ? e.message : '';
      setErr(m === 'max_received'
        ? T('Cinq documents reçus au maximum.', 'Five received documents at most.')
        : friendlyError(e, lang));
    }
    setRecvBusy(false);
  };

  const save = async () => {
    /* ── DÉCRIRE CE QUI MANQUE, ET LE MONTRER ──
       A red line at the top saying "the person is required" makes you hunt
       for the field. The field itself says so, and the form scrolls to it. */
    if (!person.trim()) {
      setMissing(['person']);
      setErr(T('La personne concernée est obligatoire.', 'The person concerned is required.'));
      document.querySelector('.fl.req input')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    if (pt && !onDossier) {
      setErr('Choose the dossier: in Portugal every request belongs to one.');
      return;
    }
    setMissing([]);
    setBusy(true); setErr('');
    try {
      const body: Record<string, unknown> = {
        person_concerned: person.trim(),
        numero: numero.trim() || null,
        acte_type_id: typeId === '' ? null : typeId,
        authority_id: authorityId === '' ? null : authorityId,
        status_id: statusId === '' ? undefined : statusId,
        /* La liste entière ; le serveur en tire `assigned_to` (la première). */
        assigned_emails: team.map((p) => p.email).filter(Boolean),
        reference: reference_.trim() || null,
        demande_channel: channel || null,
        date_demande: dDemande || null,
        date_emission: dEmission || null,
        date_reception: dReception || null,
        date_expiration: dExpiration || null,
        comments: comments.trim() || null,
        is_foreign: foreign,
        ephoto_code: ephoto.trim() || null,
        /* Sent on edit too: moving a demande to another dossier — or off one
           entirely — is the correction this form exists for. */
        dossier_id: onDossier || null,
      };
      if (pt) body.request_line = requestLine.trim() || null;
      if (editing) {
        await actesUpdateDemande(initial!.id, body);
      } else {
        const { data } = await actesCreateDemande(body);
        /* LA DEMANDE EST DÉJÀ CRÉÉE : ELLE NE PEUT PLUS ÉCHOUER.
           Les pièces se rattachent après coup, et le serveur en refuse une
           sixième (409 max_related). Cette erreur remontait au `catch` du
           dessous : le panneau « Demande créée » ne s'affichait jamais, la
           fenêtre restait ouverte sur un message rouge — et le geste évident,
           re-presser « Créer », en fabriquait une DEUXIÈME. On confirme la
           création, et on dit à côté ce qui n'a pas pu être joint. */
        const failed: string[] = [];
        for (const d of stagedDocs) {
          try {
            await actesAddDocument(data.id, {
              file_path: d.file_path, file_name: d.file_name, file_mime: d.file_mime, category: d.category,
            });
          } catch (e) {
            failed.push(d.file_name || '—');
            console.warn('[ACTE_DOC_ATTACH_FAILED]', e);
          }
        }
        if (failed.length) {
          setErr(T(
            `Demande créée, mais ${failed.length} pièce(s) n'ont pas pu être jointes : ${failed.join(', ')}. Rouvrez la fiche pour les rattacher.`,
            `Request created, but ${failed.length} file(s) could not be attached: ${failed.join(', ')}. Reopen the record to attach them.`,
          ));
        }
        await qc.invalidateQueries({ queryKey: ['actes'] });
        setBusy(false);
        setMade({ id: data.id, person: person.trim(), dossierId: data.dossier_id ?? dossierId ?? null });
        /* LE CLIGNOTEMENT ATTEND QU'ON FERME.
           Il partait ICI, alors que le panneau « Demande créée » couvrait
           encore l'écran avec son voile : la rangée clignotait derrière, deux
           secondes, et c'était fini avant qu'on ait fermé. Personne ne l'a
           jamais vu. L'id est gardé et annoncé à la fermeture — voir
           `finish()`. */
        return;
      }
      await qc.invalidateQueries({ queryKey: ['actes'] });
      onClose();
    } catch (e) { setErr(friendlyError(e, lang)); }
    setBusy(false);
  };

  /* On sort d'ici : c'est MAINTENANT que la rangée qu'on vient de créer doit
     clignoter, quand elle est enfin visible. */
  const finish = () => {
    if (made) markNewActe(made.id);
    onClose();
  };

  const remove = async () => {
    if (!initial) return;
    if (!window.confirm(T('Mettre cette demande à la corbeille ?', 'Move this request to the trash?'))) return;
    setBusy(true);
    try {
      await actesTrashDemande(initial.id);
      await qc.invalidateQueries({ queryKey: ['actes'] });
      /* Nothing is destroyed — it goes to the corbeille. The toast is the
         short way back; the Corbeille tab is the long one. */
      pushUndo({
        label: T('Demande mise à la corbeille', 'Request moved to the trash'),
        detail: initial.person_concerned,
        undo: async () => { await actesTrashDemande(initial.id, true); await qc.invalidateQueries({ queryKey: ['actes'] }); },
      });
      onClose();
    } catch (e) { setErr(friendlyError(e, lang)); }
    setBusy(false);
  };

  return createPortal(
    <div className="scrim actes-skin" onClick={finish}>
      {/* `maxWidth` NE SUFFISAIT PAS. `.svc-modal` porte `width: min(640px,
          100%)` : relever le MAXIMUM ne change rien tant que la largeur elle
          -même est bloquée à 640. D'où la classe, qui pose les deux. */}
      <div className="modal wide" onClick={(e) => e.stopPropagation()}>
        {made ? (
          /* ── Créée. Et maintenant ? ── */
          <div className="svc-modal-body">
            <div className="svc-modal-tag">{T('Demande d’acte', 'Civil-record request')}</div>
            <h2>{T('Demande créée', 'Request created')}</h2>
            <p className="muted-note ok-note">
              <KBIcon name="check" size={13} /> {made.person}
            </p>
            <div className="nt-actions">
              <button className="btn btn-ghost" onClick={finish}>{T('Fermer', 'Close')}</button>
              <button className="btn btn-ghost" onClick={() => {
                /* Filing several in a row is the normal case: the form comes
                   back empty except for what they all share. */
                setMade(null); setPerson(''); setNumero(''); setReference('');
                setComments(''); setStagedDocs([]);
                setDDemande(''); setDEmission(''); setDReception(''); setDExpiration('');
              }}>
                <KBIcon name="plus" size={13} /> {T('En créer une autre', 'Create another')}
              </button>
              {made.dossierId && (
                <button className="btn" onClick={() => {
                  /* The store is read ONCE, when the Actes section mounts. From
                     inside Actes nothing re-reads it, so this button set a
                     value and closed — doing nothing, and leaving the id
                     pending so the NEXT visit silently jumped into a stale
                     dossier. The section listens for this event and opens the
                     dossier itself; the focus is set first for the case where
                     we are not in Actes at all. */
                  markNewActe(made.id);
                  setActesFocus(made.dossierId);
                  window.dispatchEvent(new CustomEvent('lexidy:open-actes'));
                  window.dispatchEvent(new CustomEvent('lexidy:actes-open-dossier', { detail: { id: made.dossierId } }));
                  onClose();
                }}>
                  <KBIcon name="ext" size={13} /> {T('Voir le dossier', 'Open the dossier')}
                </button>
              )}
            </div>
          </div>
        ) : (
        <>
        {/* La cabecera de la maqueta: la eyebrow dice de qué se trata, el
            título dice qué se va a hacer, y la × vive dentro de la cabecera —
            no flotando sobre una esquina. */}
        <div className="modal-h">
          <div>
            <div className="eyebrow">{T('Demande d’acte', 'Civil-record request')}</div>
            <h3>{editing ? T('Modifier la demande', 'Edit request') : T('Nouvelle demande', 'New request')}</h3>
          </div>
          <button className="rb x" onClick={finish} aria-label={T('Fermer', 'Close')}>
            <KBIcon name="x" size={14} />
          </button>
        </div>
        <div className="modal-b">

          {/* ── LE DOSSIER ──
              A picker is for a question that is still open. When the dossier
              is already known — you pressed "new request" from inside it, or
              from the Actes block of its own case — the question is answered,
              and a search box sitting on top of the answer only asks you to
              answer it again. (Worse: it was rendered DISABLED, which reads as
              broken rather than as settled.)
              So: the chosen dossier, and a "change" that brings the search
              back for the two cases where it is genuinely wrong — filed on the
              wrong family, or filed loose and now knowing whose it is. */}
          {/* While the dossier is being fetched, the picker must NOT flash on
              screen — a search box appearing for half a second and vanishing
              is the same "answer it again" as before, only worse. */}
          {on && !dossier.data && dossier.isLoading && !swapDossier ? (
            <div className="fl">
              <span>{T('Dossier', 'Dossier')}</span>
              <div className="actes-chosen quiet skel"><span className="actes-list-main" /></div>
            </div>
          ) : dossier.data && !swapDossier ? (
            <div className="fl">
              <span>{T('Dossier', 'Dossier')}</span>
              <div className="actes-chosen quiet">
                <KBIcon name="documents" size={13} />
                <span className="actes-list-main">
                  <strong>{dossier.data.dossier.name}</strong>
                  <em>
                    {ticket.data
                      /* The model's reading, waited for if need be — never the
                         raw filing subject and never the pipeline id, which is
                         what "Nowers Citizenship by Descent France · FR -
                         Nationality/Citizenship" was made of. */
                      ? tidy.full
                      : tie
                        ? `${T('Dossier client', 'Case')} #${tie}`
                        : T('Sans dossier client', 'No case assigned')}
                  </em>
                </span>
                {/* Going to the case you are ALREADY standing on is not an
                    action, it is a loop — so the link exists everywhere except
                    there. From Actes it is the way back to the client. */}
                {/* Only when the case is REAL. The tie may be an old HubSpot
                    order number somebody typed by hand, and the board answers
                    nothing for it — the link then landed on an empty list and
                    looked broken. The ticket is already fetched above; if it
                    came back empty there is nowhere to go, so nothing is
                    offered. */}
                {tie && tie !== onTicket && ticket.data && (
                  <button type="button" onClick={() => {
                    window.dispatchEvent(new CustomEvent('lexidy:open-case', { detail: { id: tie } }));
                    onClose();
                  }}>{T('voir le dossier client', 'open the case')}</button>
                )}
                <button type="button" className="alt" onClick={() => { prevDossier.current = onDossier; setSwapDossier(true); }}>
                  {T('changer', 'change')}
                </button>
              </div>
            </div>
          ) : (
            <div className="fl">
              <span>{T('Dossier', 'Dossier')}</span>
              <Combobox lang={lang} value={onDossier || null} minChars={2}
                onChange={(v) => { setOnDossier(v === null ? '' : String(v)); if (v !== null) setSwapDossier(false); }}
                placeholder={pt ? 'Search a dossier…' : T('Chercher un dossier… (vide = sans dossier)', 'Search a dossier… (empty = no dossier)')}
                /* LA NATIONALITÉ D'ABORD. Une demande d'acte se rattache
                   presque toujours à un dossier de nationalité, pas à une
                   analyse d'éligibilité ni à une recherche d'acte. C'est un
                   ordre, pas un filtre : tout reste proposé. Le service est
                   rappelé sous le nom, pour qu'on voie POURQUOI il est là. */
                items={byDossierRank(dossiers.data ?? [], (d) => d.service_codes)
                  .map((d) => ({
                    id: d.id,
                    label: d.name,
                    sub: [d.hubspot_id ? `#${d.hubspot_id}` : null, (d.service_codes ?? [])[0] || null]
                      .filter(Boolean).join(' · ') || null,
                  }))} />
              {swapDossier && (
                <button type="button" className="actes-inline-cancel"
                  onClick={() => { setOnDossier(prevDossier.current); setSwapDossier(false); }}>
                  {T('garder celui d’avant', 'keep the previous one')}
                </button>
              )}
            </div>
          )}

          <div className="fgrid">
            <label className="fl">
              <span>{T('N° (ordre généalogique)', 'No. (family order)')}</span>
              <input value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="1a" />
            </label>
            <label className={`fl ${missing.includes('person') ? 'req' : ''}`}>
              <span>{T('Personne concernée', 'Person concerned')}{missing.includes('person') && <em className="req">{T('obligatoire', 'required')}</em>}</span>
              <input value={person} autoFocus placeholder={T('Prénom NOM', 'First LAST')}
                onChange={(e) => { setPerson(e.target.value); if (missing.length) setMissing([]); }} />
            </label>
          </div>

          {/* ── L'E-PHOTO ──
              Une photo d'identité numérique porte son code de série imprimé
              dessus, et c'est ce code qui la relie à la personne. La Réception
              (IA) le lit sur l'image au scan et le pose ici ; ce champ sert à
              le corriger, ou à le saisir quand la photo est arrivée autrement.
              Il reste vide sur tout ce qui n'est pas une e-photo. */}
          {pt ? (
            <label className="fl">
              <span>Document requested</span>
              <input value={requestLine} onChange={(e) => setRequestLine(e.target.value)}
                placeholder="e.g. Birth certificate (full copy) — Conservatória do Registo Civil" />
            </label>
          ) : (
          <label className="fl">
            <span>{T('Code e-photo', 'e-Photo code')}</span>
            <input value={ephoto} onChange={(e) => setEphoto(e.target.value)}
              placeholder={T('imprimé sur la photo — vide si ce n’en est pas une', 'printed on the photo — empty if it is not one')} />
          </label>
          )}

          <div className="fgrid">
            {/* Two hundred types and four hundred authorities: you type the
                name you already know instead of scrolling to find it. A type
                that does not exist yet is created from here, not from an
                admin screen and back. */}
            <div className="fl">
              <span>{T('Type d’acte', 'Type')}</span>
              <Combobox lang={lang} value={typeId === '' ? null : typeId}
                onChange={(v) => setTypeId(v === null ? '' : Number(v))}
                placeholder={T('Naissance, mariage, décès…', 'Birth, marriage, death…')}
                items={reference.acteTypes.filter((t) => t.is_active).map((t) => ({
                  id: t.id, label: lang === 'fr' ? t.label_fr : t.label_en,
                  sub: (pt ? t.label_short_en : t.label_short_fr) || null,
                }))}
                /* Portugal reads the shared list; France keeps it. */
                onCreate={pt ? undefined : async (label) => {
                  const { data } = await actesCreateType(label);
                  await qc.invalidateQueries({ queryKey: ['actes', 'reference'] });
                  return { id: data.id, label: data.label_fr };
                }} />
            </div>
            {!pt && (
            <div className="fl">
              <span>{T('Autorité', 'Authority')}</span>
              <Combobox lang={lang} value={authorityId === '' ? null : authorityId}
                onChange={(v) => setAuthorityId(v === null ? '' : Number(v))}
                placeholder={T('Mairie, SCEC, archives…', 'Town hall, SCEC, archives…')}
                items={reference.authorities.map((a) => ({
                  id: a.id, label: lang === 'fr' ? a.label_fr : a.label_en,
                  sub: a.department || null,
                }))}
                /* UNE MAIRIE QUI N'EXISTE PAS ENCORE SE CRÉE D'ICI.
                   Le type d'acte avait ce bouton depuis toujours ; l'autorité,
                   non — alors que c'est CELLE-LÀ qui manque tout le temps : il
                   y a une mairie par commune. On écrivait le nom, rien ne
                   s'affichait, on enregistrait, et la demande partait sans
                   autorité. La nouvelle atterrit dans l'annuaire, où elle
                   s'édite et se complète (e-mail, adresse, département) comme
                   les quatre cents autres. */
                onCreate={async (label) => {
                  const { data } = await actesSaveAuthority(null, { label_fr: label });
                  await qc.invalidateQueries({ queryKey: ['actes', 'reference'] });
                  await qc.invalidateQueries({ queryKey: ['actes', 'authorities'] });
                  return { id: data.id, label: data.label_fr };
                }} />
              <TurnaroundNote lang={lang} authorityId={authorityId === '' ? null : authorityId}
                reference={reference} data={turnaround.data ?? null} dDemande={dDemande} received={!!dReception} />
            </div>
            )}
          </div>

          <div className="fgrid">
            <label className="fl">
              <span>{T('Statut', 'Status')}</span>
              <select value={statusId} onChange={(e) => setStatusId(e.target.value ? Number(e.target.value) : '')}>
                {reference.acteStatuses.map((s) => (
                  <option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en}</option>
                ))}
              </select>
            </label>
            <div className="fl">
              <span>{team.length > 1 ? T('Responsables', 'Owners') : T('Responsable', 'Owner')}</span>
              <PeopleField lang={lang === 'fr' ? 'fr' : 'en'} people={reference.profiles} value={team} onChange={setTeam} />
            </div>
          </div>

          <div className="fgrid">
            <label className="fl">
              <span>{T('Référence', 'Reference')}</span>
              <input value={reference_} onChange={(e) => setReference(e.target.value)} />
            </label>
            <label className="fl">
              <span>{T('Demandé par', 'Requested via')}</span>
              <select value={channel} onChange={(e) => setChannel(e.target.value as '' | 'courrier' | 'email')}>
                <option value="">—</option>
                <option value="courrier">{T('Courrier', 'Post')}</option>
                <option value="email">{T('E-mail', 'E-mail')}</option>
              </select>
            </label>
          </div>

          {/* The four dates. Expiration is normally DERIVED from the emission
              when a document is read on reception; it stays editable because a
              foreign act does not always follow the six-month rule. */}
          <div className="actes-grid4">
            <label className="fl">
              <span>{T('Demandée', 'Requested')}</span>
              <input type="date" value={dDemande} onChange={(e) => setDDemande(e.target.value)} />
            </label>
            <label className="fl">
              <span>{T('Émission', 'Issued')}</span>
              <input type="date" value={dEmission} onChange={(e) => setDEmission(e.target.value)} />
            </label>
            <label className="fl">
              <span>{T('Réception', 'Received')}</span>
              <input type="date" value={dReception} onChange={(e) => setDReception(e.target.value)} />
            </label>
            <label className="fl">
              <span>{T('Expiration', 'Expires')}</span>
              <input type="date" value={dExpiration} onChange={(e) => setDExpiration(e.target.value)} />
            </label>
          </div>

          <label className="fl">
            <span>{T('Commentaires', 'Comments')}</span>
            <textarea rows={3} value={comments} onChange={(e) => setComments(e.target.value)} />
          </label>

          <label className="actes-check">
            <input type="checkbox" checked={foreign} onChange={(e) => setForeign(e.target.checked)} />
            {pt ? 'Foreign document (issued outside Portugal)' : T('Acte étranger (hors France)', 'Foreign act (outside France)')}
          </label>

          {/* LES DEUX BLOCS DE DOCUMENTS CÔTE À CÔTE.
              Empilés, ils poussaient les boutons de la fiche hors de l'écran
              et il fallait faire défiler pour enregistrer. La fiche est
              large maintenant : ils tiennent l'un à côté de l'autre. */}
          <div className="actes-fs2">
          {/* ── Le document reçu ── */}
          {editing && (
            <div className="fsec">
              <span className="eyebrow">{T('Document reçu', 'Received document')}</span>
              {initial?.file_path ? (
                <div className="actes-docs-list">
                  <div className="actes-docs-one">
                    <KBIcon name="documents" size={14} />
                    <span className="actes-docs-n" title={initial.file_name ?? ''}>{initial.file_name || T('Document reçu', 'Received document')}</span>
                    {recv?.uploader_name && <span className="dim">· {recv.uploader_name}</span>}
                    <button type="button" className="actes-row-act" title={T('Voir', 'View')} onClick={() => openReceived()}>
                      <KBIcon name="eye" size={13} />
                    </button>
                  </div>
                </div>
              ) : (
                <p className="muted-note">{T('Aucun document reçu pour l’instant.', 'No received document yet.')}</p>
              )}

              <ActeDocuments key={`r${recvKey}`} lang={lang} acteId={initial!.id} source="ai_reception"
                canManage={false} isSuperAdmin={reference.me.isSuper} hideEmpty />

              {reference.me.isSuper && (
                <div className="actes-docs-add">
                  <button type="button" className="btn btn-sm btn-ghost" disabled={recvBusy} onClick={() => recvInput.current?.click()}>
                    <KBIcon name="ai" size={13} /> {recvBusy ? T('Envoi…', 'Uploading…') : T('Joindre un acte reçu', 'Attach a received act')}
                  </button>
                  <label className="actes-check">
                    <input type="checkbox" checked={recvDates} onChange={(e) => setRecvDates(e.target.checked)} />
                    {T('Mettre les dates à jour à la lecture', 'Update the dates when reading it')}
                  </label>
                  <input ref={recvInput} type="file" hidden accept="application/pdf,image/png,image/jpeg,image/webp"
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void attachReceived(f); }} />
                </div>
              )}
              {recvNote && <p className="muted-note ok-note">{recvNote}</p>}
            </div>
          )}

          {/* ── Les documents liés ── */}
          <div className="fsec">
            <span className="eyebrow">{T('Documents liés', 'Related documents')}</span>
            {editing
              ? <ActeDocuments lang={lang} acteId={initial!.id} source="manual"
                  canManage={reference.me.isStaff} isSuperAdmin={reference.me.isSuper} />
              : <ActeDocuments lang={lang} onStagedChange={setStagedDocs}
                  canManage={reference.me.isStaff} isSuperAdmin={reference.me.isSuper} />}
          </div>
          </div>

          {err && <p className="muted-note ni-err">⚠ {err}</p>}
        </div>

        {/* `.modal-f` : la corbeille à gauche, l'action à droite, et le
            `.spacer` entre les deux — comme partout ailleurs. */}
        <div className="modal-f">
          {editing && reference.me.isStaff && (
            <button className="btn danger" onClick={() => void remove()}>{T('Corbeille', 'Trash')}</button>
          )}
          <span className="spacer" />
          <button className="btn" onClick={onClose}>{T('Annuler', 'Cancel')}</button>
          <button className="btn btn-p" disabled={busy || !person.trim()} onClick={() => void save()}>
            {busy ? '…' : editing ? T('Enregistrer', 'Save') : T('Créer', 'Create')}
          </button>
        </div>
        </>
        )}
      </div>
      {view && <DocViewer lang={lang} load={view} onClose={() => setView(null)} />}
    </div>,
    document.body,
  );
}

/* ── LES DÉLAIS OBSERVÉS ──
   Trois paliers, et l'écran dit toujours sur combien de demandes le chiffre
   repose : l'autorité elle-même quand elle a au moins trois demandes
   complètes ; sinon sa catégorie (mairie, SCEC, archives…) ; sinon rien —
   « pas encore de mesure » vaut mieux qu'une médiane sur un cas. Quand la
   demande est partie et pas revenue, la relance suggérée est le 75e centile
   ajouté à la date de départ : trois demandes sur quatre sont rentrées avant. */
const CAT_LABEL: Record<string, [string, string]> = {
  mairie: ['les mairies', 'town halls'], scec: ['le SCEC', 'the SCEC'], archives: ['les archives', 'archives'],
  foreign: ['les autorités étrangères', 'foreign authorities'], other: ['les autres autorités', 'other authorities'],
};
const plusDays = (day: string, n: number): string => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + Math.round(n));
  return d.toISOString().slice(0, 10);
};
const fmtDay = (day: string, lang: Lang): string =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

function TurnaroundNote({ lang, authorityId, reference, data, dDemande, received }: {
  lang: Lang; authorityId: number | null; reference: ActesReference; data: ActesTurnaround | null;
  dDemande: string; received: boolean;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  if (!authorityId || !data) return null;
  const own = data.authorities[String(authorityId)];
  const cat = reference.authorities.find((a) => a.id === authorityId)?.category ?? 'other';
  const catStat = data.categories[cat] ?? data.categories.other;
  const stat = own && own.n >= 3 ? { ...own, scope: 'own' as const }
    : catStat && catStat.n >= 3 ? { ...catStat, scope: 'cat' as const } : null;
  const j = (n: number) => `${n % 1 === 0 ? n : n.toFixed(1)} ${T('j', 'd')}`;
  const [catFr, catEn] = CAT_LABEL[cat] ?? CAT_LABEL.other;
  const suggest = stat && dDemande && !received ? plusDays(dDemande, stat.p75) : '';
  const today = new Date().toISOString().slice(0, 10);
  return (
    <p className="actes-field-note actes-turnaround" data-scope={stat?.scope ?? 'none'}>
      {!stat ? (
        T('Délai : pas encore de mesure (moins de trois demandes complètes).',
          'Turnaround: no measurement yet (fewer than three completed requests).')
      ) : stat.scope === 'own' ? (
        T(`Délai observé ici : médiane ${j(stat.median)}, 3 sur 4 sous ${j(stat.p75)} (${stat.n} demandes).`,
          `Observed here: median ${j(stat.median)}, 3 in 4 under ${j(stat.p75)} (${stat.n} requests).`)
      ) : (
        T(`Pas assez de mesures ici ; pour ${catFr} : médiane ${j(stat.median)}, 3 sur 4 sous ${j(stat.p75)} (${stat.n} demandes).`,
          `Not enough data here; for ${catEn}: median ${j(stat.median)}, 3 in 4 under ${j(stat.p75)} (${stat.n} requests).`)
      )}
      {own && own.pending > 0 && (
        <> {T(`${own.pending} en attente chez elle.`, `${own.pending} still pending with them.`)}</>
      )}
      {suggest && (
        <> <b>{suggest < today
          ? T(`Relance conseillée : dès maintenant (le délai habituel est dépassé depuis le ${fmtDay(suggest, lang)}).`,
              `Chase now: the usual turnaround passed on ${fmtDay(suggest, lang)}.`)
          : T(`Relance conseillée le ${fmtDay(suggest, lang)} si rien n'est arrivé.`,
              `Chase on ${fmtDay(suggest, lang)} if nothing has arrived.`)}</b></>
      )}
    </p>
  );
}
